import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';

import useDataSource from "../../../../hooks/useDataSource";
import ClearFilterBtn from "../../../application/ClearFilterBtn";

import {
    LinearProgress,
    styled,
    Typography,
    Avatar,
    CardHeader,
    CardContent,
    Card,
    Tooltip,
} from "@mui/material";
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import TrendingFlatOutlinedIcon from '@mui/icons-material/TrendingFlatOutlined';
import {areLoadDependenciesMet, getFieldFormatters} from "../../../../utilities/DataSourceUtilities";
import {
    formatDate, formatDaysRemaining, formatDuration,
    formatNumber,
    formatStyle,
    formatSuffix,
    formatText
} from "../../../../utilities/FormatUtilities";
import {useDispatch, useSelector} from "react-redux";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent} from "../../../../redux/page/pageActions";

const PREFIX = 'PerformanceIndicator';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardHeader: `${PREFIX}-cardHeader`,
    circleContainer: `${PREFIX}-circleContainer`,
    circleKPIContainer: `${PREFIX}-circleKPIContainer`,
    iconContainer: `${PREFIX}-iconContainer`,
    iconKPIContainer: `${PREFIX}-iconKPIContainer`,
    icon: `${PREFIX}-icon`,
    valueContainer: `${PREFIX}-valueContainer`,
    valueKPIContainer: `${PREFIX}-valueKPIContainer`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none'
    },

    [`& .${classes.circleContainer}`]: {
        width: theme.spacing(8),
        height: theme.spacing(8),
        borderRadius: '50%',
        display: 'block',
        position: 'absolute',
        left: '50%',
        top: '50%',
        margin: '-35px 0 0 -35px',
        color: 'black'
        // backgroundColor: '#00e676'
    },

    [`& .${classes.circleKPIContainer}`]: {
        width: theme.spacing(8),
        height: theme.spacing(8),
        borderRadius: '50%',
        color: 'black'
    },

    [`& .${classes.iconContainer}`]: {
        height: '50%',
        width: '100%',
        textAlign: 'center'
    },

    [`& .${classes.iconKPIContainer}`]: {
        height: '100%',
        width: '100%',
        textAlign: 'center',
        margin: '10px'
    },

    [`& .${classes.icon}`]: {
        fontSize: '24px',
        verticalAlign: 'middle',
        // marginTop: '10px'
        height: '100%'
    },

    [`& .${classes.valueContainer}`]: {
        height: '50%',
        width: '100%',
        textAlign: 'center'
    },

    [`& .${classes.valueKPIContainer}`]: {
        height: '50%',
        width: '100%',
        textAlign: 'center',
        margin: '10px'
    }
}));

export default function PerformanceIndicator({id, configuration, xId, dataSourceIndex, onLoading, onLoaded}) {

	const dispatch = useDispatch();
	const { t, i18n } = useTranslation();

    // App State
    const filters = useSelector(state => state.data.filters);
    const ragStyle = useSelector(state => state.config.data.ragStyle);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [value, setValue] = useState(null);
    const [altValue, setAltValue] = useState(null);
    const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
    const [performanceValue, setPerformanceValue] = useState('');
    const [cpiSymbol, setCpiSymbol] = useState('$');
    const [kpiSymbol, setKpiSymbol] = useState('');
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});

	const dataSourceName = compConfig.dataSource?.name !== undefined ? compConfig.dataSource?.name : '';
	const dependencies = compConfig.dataSource?.dependencies ? compConfig.dataSource?.dependencies : null;
	const field = compConfig.dataSource?.fields?.field !== undefined ? compConfig.dataSource?.fields?.field : '';
	const element = compConfig.element ? compConfig.element : {};
	const showValue = compConfig.element?.showValue !== undefined ? compConfig.element.showValue : true;
	const showAltValue = compConfig.element?.showAltValue !== undefined ? compConfig.element.showAltValue : {enabled: false, field: null};
	const showSymbol = compConfig.element?.showSymbol !== undefined ? compConfig.element.showSymbol : true;
	const styles = compConfig?.element?.styles;
	const dataIndex = dataSourceIndex !== undefined ? dataSourceIndex : 0;

	let dataSource = useDataSource(dataSourceName);

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
        }
    }, [dataSource.fetched]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// Titles & Resized
	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Performance Indicator Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'PerformanceIndicator'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    // Data
    useEffect(() => {

        if (dataSource.data && dataSource.fetched) {

            // If our dependencies are not met then we're
            // loading/no chance until they are
            if (!areLoadDependenciesMet(dependencies, filters)) {
                return;
            }

            let data =[];
            data = dataSource.data;

            if ((data.length === 1) || (dataSourceIndex !== undefined)) {
                // Make sure data filters/refreshes have occurred

                // If data length is ONE and not being passed an index
                // OR if the data length > than ONE and being passed an index
                if (((data.length === 1) && (dataSourceIndex === undefined)) || (data.length > (dataSourceIndex))) {
                    const formatters = getFieldFormatters(dataSource, field.name); // Used when formatters are on the data source

                    let formattedValue = data[dataIndex][field.name];
                    // console.log('PerformanceIndicator: Before formatters: ', formattedValue);
                    for (const formatter of formatters) {
                        if (formatter.type.toLowerCase() === 'date') {
                            formattedValue = formatDate(formattedValue, formatter.config);
                        } else if (formatter.type.toLowerCase() === 'duration') {
                            formattedValue = formatDuration(data[0].min, data[0].max, formatter.config);
                        } else if (formatter.type.toLowerCase() === 'daysremaining') {
                            formattedValue = formatDaysRemaining(data[0].daysremaining, formatter.config);
                        } else if (formatter.type.toLowerCase() === 'number') {
                            formattedValue = formatNumber(formattedValue, formatter.config);
                        } else if (formatter.type.toLowerCase() === 'style') {
                            formattedValue = formatStyle(formattedValue, formatter.config, data[dataIndex][field.name], formatter.condition, false);
                        } else if (formatter.type.toLowerCase() === 'text') {
                            formattedValue = formatText(formattedValue, formatter.config);
                        } else if (formatter.type.toLowerCase() === 'suffix') {
                            formattedValue = formatSuffix(formattedValue, formatter.config);
                        } else {

                        }
                    }

                    // Get Alternate Value also...
                    if (showAltValue.enabled) {
                        const altValueFormatters = getFieldFormatters(dataSource, showAltValue.field); // Used when formatters are on the data source
                        let altValueFormattedValue = data[dataIndex][showAltValue.field];
                        // console.log('PerformanceIndicator: Before formatters: ', formattedValue);
                        for (const formatter of altValueFormatters) {
                            if (formatter.type.toLowerCase() === 'date') {
                                altValueFormattedValue = formatDate(altValueFormattedValue, formatter.config);
                            } else if (formatter.type.toLowerCase() === 'duration') {
                                altValueFormattedValue = formatDuration(data[0].min, data[0].max, formatter.config);
                            } else if (formatter.type.toLowerCase() === 'daysremaining') {
                                altValueFormattedValue = formatDaysRemaining(data[0].daysremaining, formatter.config);
                            } else if (formatter.type.toLowerCase() === 'number') {
                                altValueFormattedValue = formatNumber(altValueFormattedValue, formatter.config);
                            } else if (formatter.type.toLowerCase() === 'style') {
                                altValueFormattedValue = formatStyle(altValueFormattedValue, formatter.config, data[dataIndex][showAltValue.field], formatter.condition, false);
                            } else if (formatter.type.toLowerCase() === 'text') {
                                altValueFormattedValue = formatText(altValueFormattedValue, formatter.config);
                            } else if (formatter.type.toLowerCase() === 'suffix') {
                                altValueFormattedValue = formatSuffix(altValueFormattedValue, formatter.config);
                            } else {

                            }
                        }
                        setAltValue(altValueFormattedValue);
                    }

                    const performanceEquality = getRangeVal(element.type.toLowerCase(), data[dataIndex][field.name], element.condition);

                    if (element.type.toLowerCase() === 'cpi' || element.type.toLowerCase() === 'spi') {
                        setValue(formattedValue);
                        setPerformanceValue(performanceEquality === 'equal' ? ragStyle.amber : (performanceEquality === 'greater' ? ragStyle.green : (performanceEquality === 'less' ? ragStyle.red : ragStyle.amber)));
                    } else if (element.type.toLowerCase() == 'kpi') {
                        showValue ? setValue(formattedValue) : setValue(null);
                        setPerformanceValue(performanceEquality === 'amber' ? ragStyle.amber : (performanceEquality === 'green' ? ragStyle.green : (performanceEquality === 'red' ? ragStyle.red : ragStyle.amber)));
                        if (showSymbol) {
                            setKpiSymbol(performanceEquality === 'amber' ?
                                <TrendingFlatOutlinedIcon className={classes.icon}/> : (performanceEquality === 'green' ?
                                    <ThumbUpOutlinedIcon className={classes.icon}/> : (performanceEquality === 'red' ?
                                        <ThumbDownOutlinedIcon className={classes.icon}/> :
                                        <TrendingFlatOutlinedIcon className={classes.icon}/>)));
                        } else {
                            setKpiSymbol(null);
                        }
                        setTitle(data[dataIndex][element.titleFields.title]);
						setSubTitle(data[dataIndex][element.titleFields.subtitle]);
                    } else {
                        setPerformanceValue(performanceEquality === 'amber' ? ragStyle.amber : (performanceEquality === 'green' ? ragStyle.green : (performanceEquality === 'red' ? ragStyle.red : ragStyle.amber)));
                    }
                }
            } else {
                // setValue("--"); // TODO: Can we set this differently, i.e. from config?
                setPerformanceValue(null);
            }
        } else {
            // setValue("--"); // TODO: Can we set this differently, i.e. from config?
            setPerformanceValue(null);
        }
    }, [dataSource.data]);

    useEffect(() => {
        // Get CPI Currency Symbol

        //console.log(i18n.language);
        //console.log(i18n.getResourceBundle(i18n.language, 'currencysymbol').key);
        let currencysymbol = i18n.getResourceBundle(i18n.language, 'currencysymbol').key

        // https://stackoverflow.com/a/53749034/14708895
        const getCurrencySymbol = (locale, currency) =>
            (0).toLocaleString(locale,
                {
                    style: 'currency',
                    currency,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).replace(/\d/g, '').trim();
        // console.log(getCurrencySymbol(i18n.language, currencysymbol));

        setCpiSymbol(getCurrencySymbol(i18n.language, currencysymbol));
    }, [t]);

    function getRangeVal(type, initVal, condition) {

        // console.log('--> FormatUtilities: getGreaterEqualLess()');
        // console.log('TYPE: ', type);
        // console.log('INITIAL VALUE: {0} ({1})'.format(initVal, typeof initVal));
        // console.log('CONDITION: ', condition);

        if (type === 'spi' || type === 'spi') {
            if (condition.type.toLowerCase() === 'range') {
                if (initVal >= parseFloat(condition.value.min) && initVal <= parseFloat(condition.value.max)) {
                    return "equal";
                } else if (initVal >= parseFloat(condition.value.min) && initVal >= parseFloat(condition.value.max)) {
                    return "greater";
                } else if (initVal <= parseFloat(condition.value.min) && initVal <= parseFloat(condition.value.max)) {
                    return "less";
                } else {
                    return '';
                }
            } else if (condition.type.toLowerCase() === 'value') {
                if (initVal === parseFloat(condition.value)) {
                    return "equal";
                } else if (initVal > parseFloat(condition.value)) {
                    return "greater";
                } else if (initVal < parseFloat(condition.value)) {
                    return "less";
                } else {
                    return '';
                }
            } else if (condition.type.toLowerCase() === 'ragmatch') {
                if (initVal.toString().toLowerCase() === 'red') {
                    return "equal";
                } else if (initVal.toString().toLowerCase() === 'amber') {
                    return "greater";
                } else if (initVal.toString().toLowerCase() === 'green') {
                    return "less";
                } else {
                    return '';
                }
            } else {
                return '';
            }
        } else if (type === 'kpi') {
            if (condition.type.toLowerCase() === 'ragmatch') {
                if (initVal.toString().toLowerCase() === 'red') {
                    return "red";
                } else if (initVal.toString().toLowerCase() === 'amber') {
                    return "amber";
                } else if (initVal.toString().toLowerCase() === 'green') {
                    return "green";
                } else {
                    return '';
                }
            } else {
                return '';
            }
        } else {
            if (condition.type.toLowerCase() === 'ragmatch') {
                if (initVal.toString().toLowerCase() === 'red') {
                    return "red";
                } else if (initVal.toString().toLowerCase() === 'amber') {
                    return "amber";
                } else if (initVal.toString().toLowerCase() === 'green') {
                    return "green";
                } else {
                    return '';
                }
            } else {
                return '';
            }
        }
    };

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'hidden'
			,padding: '0px'
			//,display: 'flex'
			,flexDirection: 'column'
			,justifyContent: 'center'
		});

		if (title && subtitle) {
			// Default Style
			newCardHeaderStyle.height = '72px';
			newCardContentStyle.height = 'calc(100% - 72px)';
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 12px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 12px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    // Set background color based upon performance
    let performanceColor = {
        backgroundColor: 'rgb(255,255,255,0)',
        borderStyle: 'solid',
        borderWidth: 'thin'
    }
    if (performanceValue) {
        performanceColor = {
            backgroundColor: performanceValue
        }
    }

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
                <div className={classes.linearProgress}></div>
            }
            <Card style={{height: '100%', boxShadow: 'none', ...styles?.kpi}}>
                <CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{variant: 'subtitle2', color: 'textPrimary'}}
                            title={t(title)}
                            subheaderTypographyProps={{variant: 'body2', color: 'textSecondary'}}
                            subheader={t(subtitle)}>
                </CardHeader>
                <CardContent style={cardContentStyle}>
                    <Typography variant="h5" gutterBottom align="center" style={{position: 'relative', top: '0px', width: '100%', height: '100%', marginTop: '3px'}}>
                        {/*CPI*/}
                        {element.type.toLowerCase() === 'cpi' &&
                            <div style={{display: 'inline-flex', alignItems: 'center'}}>
                                <Tooltip title="Cost Performance Index" arrow placement="bottom">
                                    <Avatar className={classes.circleContainer} style={performanceColor}>
                                        <div className={classes.iconContainer} style={{paddingTop: '10px'}}>
                                            <span className={classes.icon}>{cpiSymbol}</span>
                                        </div>
                                        <div className={classes.valueContainer}>
                                            <Typography variant={'body2'}>{value}</Typography>
                                        </div>
                                    </Avatar>
                                </Tooltip>
                            </div>
                        }
                        {/*SPI*/}
                        {element.type.toLowerCase() === 'spi' &&
                            <div style={{display: 'inline-flex', alignItems: 'center'}}>
                                <Tooltip title="Schedule Performance Index" arrow placement="bottom">
                                    <Avatar className={classes.circleContainer} style={performanceColor}>
                                        <div className={classes.iconContainer}>
                                            <CalendarTodayOutlined className={classes.icon}/>
                                        </div>
                                        <div className={classes.valueContainer}>
                                            <Typography variant={'body2'}>{value}</Typography>
                                        </div>
                                    </Avatar>
                                </Tooltip>
                            </div>
                        }
                        {/*KPI*/}
                        {element.type.toLowerCase() === 'kpi' &&
                            <div style={{display: 'inline-flex', alignItems: 'center'}}>
                                <Tooltip title="Key Performance Indicator" arrow placement="bottom">
                                    <Avatar className={classes.circleKPIContainer} style={performanceColor}>
                                        <div className={classes.iconKPIContainer}>
                                            {kpiSymbol}
                                        </div>
                                        {value &&
                                            <div className={classes.valueContainer}>
                                                <Typography variant={'body2'}>{value}</Typography>
                                            </div>
                                        }
                                    </Avatar>
                                </Tooltip>
                                {showAltValue.enabled &&
                                    <div className={classes.valueKPIContainer}>
                                        <Typography variant={'body2'}>{altValue}</Typography>
                                    </div>
                                }
                            </div>
                        }
                    </Typography>
                </CardContent>
            </Card>
        </Root>
    );
}
